import { loadClientEnv } from "../config/env";
import type { UIError, UIErrorKind } from "./types";

// ProblemDetail の最小形状。2.3 で実装される ApiError クラスへの直接依存を避け、
// duck typing で判定できるようにローカルに定義する（design.md errors 層 参照）。
type ProblemDetailLike = {
  type: string;
  title: string;
  status: number;
  detail: string;
  errors?: Array<{ field: string; message: string }>;
};

type ApiErrorLike = {
  status: number;
  problem: ProblemDetailLike;
};

// 各 kind の表示テンプレート。error-ux (Phase 4) でユーザー向け文言を正規化。
// body はどの kind でも「何が起きたか + 次に何をすべきか」の 2 文構成に統一。
type UIErrorTemplate = {
  title: string;
  body: string;
  retryable: boolean;
  requiresRestart: boolean;
};

const TEMPLATES: Record<UIErrorKind, UIErrorTemplate> = {
  validation: {
    title: "入力に問題があります",
    body: "選択できない操作です。最新のシーンで再度お試しください。",
    retryable: false,
    requiresRestart: false,
  },
  notFound: {
    title: "このプレイラインは継続できません",
    body: "プレイラインが見つかりません。新しい物語を開始してください。",
    retryable: false,
    requiresRestart: true,
  },
  conflict: {
    title: "表示内容が更新されました",
    body: "画面の内容が更新されました。最新のシーンを表示しています。",
    retryable: false,
    requiresRestart: false,
  },
  invalidAction: {
    title: "この選択はもう有効ではありません",
    body: "その操作は現在のシーンでは実行できません。画面を最新化しています。",
    retryable: false,
    requiresRestart: false,
  },
  aiTimeout: {
    title: "生成に時間がかかっています",
    body: "AI の生成に時間がかかっています。もう一度お試しください。",
    retryable: true,
    requiresRestart: false,
  },
  network: {
    title: "通信に問題があります",
    body: "接続できませんでした。通信環境を確認してもう一度お試しください。",
    retryable: true,
    requiresRestart: false,
  },
  unknown: {
    title: "予期しないエラーが発生しました",
    body: "予期しないエラーが発生しました。時間をおいてもう一度お試しください。",
    retryable: true,
    requiresRestart: false,
  },
};

// ProblemDetail.type URI と UIErrorKind のマッピング。
// 未知 type は unknown にフォールバックする（Req 6.5）。
const TYPE_TO_KIND: Record<string, UIErrorKind> = {
  "/errors/bad-request": "validation",
  "/errors/not-found": "notFound",
  "/errors/conflict": "conflict",
  "/errors/invalid-action": "invalidAction",
  "/errors/ai-timeout": "aiTimeout",
  // worldline-palimpsest (Phase 3-1) 未踏分岐 AI 提案のエラー。
  // 両者とも retryable な AI 失敗 (UI は "?" を維持して再試行誘導) なので aiTimeout 系に集約。
  "/errors/branch-preview-timeout": "aiTimeout",
  "/errors/branch-preview-failed": "aiTimeout",
};

// ApiError 形状かどうかを duck typing で判定する。
// 2.3 の実装（class ApiError）に依存せず、{status, problem:{type,...}} 構造のみを見る。
function isApiErrorLike(value: unknown): value is ApiErrorLike {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  if (typeof candidate.status !== "number") return false;
  const problem = candidate.problem;
  if (typeof problem !== "object" || problem === null) return false;
  const p = problem as Record<string, unknown>;
  return typeof p.type === "string" && typeof p.detail === "string";
}

/**
 * error-ux (Phase 4) 拡張: buildUIError は feature flag `friendlyErrorEnabled` を考慮する。
 *
 * - `friendlyErrorEnabled === true` (default): body は TEMPLATES を優先、server の detail は
 *   UIError.serverDetail に保持 (ErrorBanner の debug chip で可視化)。技術文字列が
 *   UI 本体にリークしない。
 * - `friendlyErrorEnabled === false`: 旧挙動。detail が非空なら body を detail で上書き、
 *   `kind === "unknown"` のときは `${template.body}（${detail}）` の括弧書き。serverDetail は
 *   undefined。既存 DOM / 文言と bit-by-bit 互換。
 *
 * `options.friendlyErrorEnabled` 未指定時は `loadClientEnv().friendlyErrorEnabled` を読む。
 * テスト用に option で明示的に override 可能。
 */
export type BuildUIErrorOptions = {
  friendlyErrorEnabled?: boolean;
};

export function buildUIError(
  kind: UIErrorKind,
  detail?: string,
  options?: BuildUIErrorOptions,
): UIError {
  const template = TEMPLATES[kind];
  const flagOn =
    options?.friendlyErrorEnabled ?? loadClientEnv().friendlyErrorEnabled;

  if (flagOn) {
    // 新挙動: template body を優先、detail は serverDetail に保持
    const base: UIError = {
      kind,
      title: template.title,
      body: template.body,
      retryable: template.retryable,
      requiresRestart: template.requiresRestart,
    };
    if (detail !== undefined && detail.length > 0) {
      return { ...base, serverDetail: detail };
    }
    return base;
  }

  // 旧挙動 (flag off): detail で body 上書き、unknown だけ括弧書き補足
  const body =
    detail !== undefined && detail.length > 0
      ? kind === "unknown"
        ? `${template.body}（${detail}）`
        : detail
      : template.body;
  return {
    kind,
    title: template.title,
    body,
    retryable: template.retryable,
    requiresRestart: template.requiresRestart,
  };
}

/**
 * ProblemDetail / ネットワーク例外 / 未知のエラーを UIError に正規化する純関数。
 * - ApiError 形状: problem.type URI で kind を決定
 * - TypeError: fetch 失敗とみなし kind=network
 * - その他: kind=unknown
 */
export function mapProblemDetail(err: unknown): UIError {
  if (isApiErrorLike(err)) {
    const kind = TYPE_TO_KIND[err.problem.type] ?? "unknown";
    return buildUIError(kind, err.problem.detail);
  }

  // fetch 失敗は TypeError として浮いてくる（MDN: Failed to fetch）
  if (err instanceof TypeError) {
    return buildUIError("network");
  }

  return buildUIError("unknown");
}
